import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Form } from "react-bootstrap";
import Card from "../../../components/Card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import Loader from "../../../components/partials/components/loader";
import { getApiCall } from "../../../service/getApiCall";
import { postApiCall } from "../../../service/postApiCall";
import { toast } from "react-toastify";
import {
  API_MANAGE_GROUP_LISTING,
  API_COMPANY_LIST,          // GET /v{X}/Company
  API_BULK_GROUP_ADD,     // POST /v{X}/groups/bulk-add
} from "../../../service/url";

export default function BulkGroupAdd() {
  const [loading, setLoading] = useState(false);

  // lists
  const [groups, setGroups] = useState([]);          // [{ group_id, group_name }]
  const [company, setCompany] = useState([]);            // [{ id, name, ... }]

  // UI state
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [rows, setRows] = useState(10);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);

  useEffect(() => {
    fetchGroups();
    fetchCompany();
  }, []);

  const fetchGroups = async () => {
    try {
      const data = await getApiCall(API_MANAGE_GROUP_LISTING, {});
      setGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error("Error fetching groups");
    }
  };

  const fetchCompany = async () => {
    setLoading(true);
    try {
      const data = await getApiCall(API_COMPANY_LIST, {});
      setCompany(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error("Error fetching Company");
    } finally {
      setLoading(false);
    }
  };

  // filter company by search (name)
  const filteredCompany = useMemo(
    () =>
      company.filter((u) =>
        (u.name || "").toLowerCase().includes(searchValue.toLowerCase())
      ),
    [company, searchValue]
  );

  const handleCheckboxToggle = (companyId) => {
    setSelectedCompanyIds((prev) =>
      prev.includes(companyId) ? prev.filter((id) => id !== companyId) : [...prev, companyId]
    );
  };


  // Select-all that respects current filter
  const handleSelectAll = () => {
    const filteredIds = filteredCompany.map((u) => u.id);
    const allSelected =
      filteredIds.length > 0 && filteredIds.every((id) => selectedCompanyIds.includes(id));

    if (allSelected) {
      // remove only the filtered ones from selection
      setSelectedCompanyIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      // add all filtered ones to selection
      setSelectedCompanyIds((prev) => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  const handleBulkAdd = async () => {
    try {
      setButtonDisable(true);
      const payload = {
        company_ids: selectedCompanyIds,   // expects array of compnay ids
        group_id: selectedGroupId,   // expects selected group id
      };

      const response = await postApiCall(API_BULK_GROUP_ADD, payload);

      if (response) {
        toast.success(response.message || "Company added to group successfully");
        setSelectedCompanyIds([]);
        setSelectedGroupId(null);
      } else {
        toast.error(response?.message || "Failed to add Company");
      }
    } catch (error) {
      toast.error(error?.message || "Error adding Company to group");
    } finally {
      setButtonDisable(false);
      setConfirmVisible(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="card-title">Bulk Company Group Add</h4>
            </Card.Header>

            <Card.Body className="px-0">
              {/* Controls Row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px",
                  flexWrap: "wrap",
                }}
              >
                {/* Group selector */}
                <Dropdown
                  value={selectedGroupId}
                  options={groups.map((g) => ({
                    label: g.group_name,
                    value: g.group_id,
                  }))}
                  onChange={(e) => setSelectedGroupId(e.value)}
                  placeholder="Select Group"
                  style={{ width: 250 }}
                />

                {/* Search */}
                <Form.Control
                  style={{ width: 250, height: 40 }}
                  placeholder="Search Company..."
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />

                {/* Show X items */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginLeft: 20,
                  }}
                >
                  <span>Show</span>
                  <Dropdown
                    value={rows}
                    options={[
                      { label: "5", value: 5 },
                      { label: "10", value: 10 },
                      { label: "20", value: 20 },
                      { label: "50", value: 50 },
                    ]}
                    onChange={(e) => setRows(e.value)}
                    style={{ width: 90, height: 40, display:"flex", alignItems:"center" }}
                    placeholder="Rows"
                  />
                  <span>items</span>
                </div>

                {/* Submit */}
                <Button
                style={{background:"#3A57E8", border:"#3A57E8"}}
                  label="Add to Group"
                  disabled={selectedCompanyIds.length === 0 || !selectedGroupId || buttonDisable}
                  onClick={() => setConfirmVisible(true)}
                  className="p-button-success btn btn-primary ml-auto"
                  // className=""
                >
                  {buttonDisable && (
                    <span
                      className="spinner-border spinner-border-sm ml-2"
                      role="status"
                      aria-hidden="true"
                      style={{ marginLeft: 8 }}
                    />
                  )}
                </Button>
              </div>

              {/* Table */}
              <div className="scroll-container">
                <DataTable
                  value={filteredCompany}
                  paginator
                  rows={rows}
                  tableStyle={{ minWidth: "50rem" }}
                  emptyMessage="No Company found"
                >
                  <Column
                    header={
                      <Checkbox
                        checked={
                          filteredCompany.length > 0 &&
                          filteredCompany.every((u) => selectedCompanyIds.includes(u.id))
                        }
                        onChange={handleSelectAll}
                      />
                    }
                    headerStyle={{ width: "3rem" }}
                    body={(rowData) => (
                      <Checkbox
                        checked={selectedCompanyIds.includes(rowData.id)}
                        onChange={() => handleCheckboxToggle(rowData.id)}
                      />
                    )}
                  />
                  <Column field="id" header="Company Id" sortable />
                  <Column field="name" header="Company Name" sortable />
                </DataTable>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Confirm Modal */}
      <Dialog
        header="Confirm Bulk Add"
        visible={confirmVisible}
        style={{ width: "25vw" }}
        onHide={() => setConfirmVisible(false)}
        footer={
          <div>
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setConfirmVisible(false)}
            />
            <Button
              label="Confirm"
              className="p-button-success"
              onClick={handleBulkAdd}
              disabled={buttonDisable}
            />
          </div>
        }
      >
        <p>Are you sure you want to add {selectedCompanyIds.length} Company(s) to this group?</p>
      </Dialog>
    </div>
  );
}
